import { QA_SECTION_ORDER } from '@tandemone/types'
import type {
  SupplierQualityQuestionKey,
  SupplierQualitySection
} from '@tandemone/types'

function sectionToSlug(section: SupplierQualitySection): string {
  return section.replace(/_/g, '-')
}

export const SUPPLIER_QUALITY_SECTION_SLUG: Record<
  SupplierQualitySection,
  string
> = Object.fromEntries(
  QA_SECTION_ORDER.map(section => [section, sectionToSlug(section)])
) as Record<SupplierQualitySection, string>

export const SUPPLIER_QUALITY_SLUG_TO_SECTION: Record<
  string,
  SupplierQualitySection
> = Object.fromEntries(
  QA_SECTION_ORDER.map(section => [sectionToSlug(section), section])
)

export const SUPPLIER_QUALITY_SECTION_LABELS: Record<
  SupplierQualitySection,
  string
> = {
  management_requirements: 'Management Requirements',
  risk_assessment: 'Risk Assessment',
  nonconformance_maintenance: 'Nonconformance & Maintenance',
  inspection_measurement: 'Inspection & Measurement',
  purchasing_supplier_selection: 'Purchasing & Supplier Selection',
  continual_improvement: 'Continual Improvement'
}

export type SupplierQualityQuestionMeta = {
  question: string
  describePrompt: string
}

/**
 * refined from the legacy tandem app
 * (tandem/app/domains/onboarding/components/quality-assesment/utils.ts).
 * intent preserved, verbosity cut. describe prompts shown only when Yes.
 */
export const SUPPLIER_QUALITY_QUESTION_LABELS: Record<
  SupplierQualityQuestionKey,
  SupplierQualityQuestionMeta
> = {
  quality_policy: {
    question: 'Is your Quality Policy communicated to everyone in the company?',
    describePrompt: 'How is the Quality Policy communicated to your team?'
  },
  quality_manual: {
    question:
      'Do you maintain a Quality Manual that describes how your QMS achieves customer satisfaction?',
    describePrompt:
      'Describe the processes the QMS covers and how they support customer satisfaction.'
  },
  management_review: {
    question:
      'Does leadership review Quality Management System performance on a regular cadence?',
    describePrompt: 'How often are reviews held, and who attends?'
  },
  quality_system_verification: {
    question:
      'Do you conduct internal audits of the Quality Management System?',
    describePrompt:
      'Describe your internal audit process, frequency, and how findings are tracked.'
  },
  corrective_preventative_action: {
    question:
      'When a customer reports a nonconformance, do you identify root cause and act to prevent recurrence?',
    describePrompt: 'Describe your root-cause and preventative-action process.'
  },
  contract_review_process: {
    question: 'Do you review order requirements before acknowledging them?',
    describePrompt: 'Describe your contract review process.'
  },
  risk_assessment: {
    question:
      'Do you assess technical, tooling, and quality risks on incoming orders?',
    describePrompt: 'Describe how each risk type is evaluated.'
  },
  document_data_control: {
    question:
      'Does document control cover changes to work instructions and customer drawings?',
    describePrompt: 'Describe the change-control process.'
  },
  quality_records: {
    question:
      'Do you have a process for controlling and retaining quality records?',
    describePrompt: 'Describe how records are controlled and retained.'
  },
  control_plans_work_instructions: {
    question:
      'Are control plans and work instructions available to operators at the point of use?',
    describePrompt:
      'Describe what documentation is used and how it reaches the floor.'
  },
  nonconforming_products: {
    question:
      'Are nonconforming parts identified and dispositioned by a designated MRB team?',
    describePrompt: 'Describe how nonconforming product is handled.'
  },
  preventative_maintenance: {
    question: 'Do you run a preventive maintenance program on key equipment?',
    describePrompt: 'Describe how key equipment is identified and maintained.'
  },
  measurement_equipment_calibration: {
    question:
      'Is measurement and test equipment calibrated on a defined schedule?',
    describePrompt:
      'Describe how calibration frequency is determined and tracked.'
  },
  final_inspection: {
    question: 'Do you inspect and verify products before shipment?',
    describePrompt: 'Describe the final inspection process.'
  },
  training: {
    question:
      'Do you evaluate employees, identify training needs, and maintain training matrices?',
    describePrompt: 'Describe the evaluation and training process.'
  },
  in_person_inspections: {
    question:
      'Are in-process inspections performed against a documented inspection or control plan?',
    describePrompt: 'Describe the in-process inspection workflow.'
  },
  verification_job_set_up: {
    question:
      'Do you verify job set-ups when starting a new job or material changeover?',
    describePrompt: 'Describe how set-ups are verified.'
  },
  material_certifications: {
    question:
      'Are Certificates of Conformance or Material Certifications shipped with each order?',
    describePrompt:
      'Describe the process and who signs off on the certificates.'
  },
  quality_requirements: {
    question:
      'Are customer quality and drawing requirements communicated to your Quality and Production teams?',
    describePrompt: 'Describe how requirements are communicated internally.'
  },
  purchasing: {
    question:
      'Do you have a process to verify that purchased materials meet your requirements?',
    describePrompt: 'Describe the purchasing and verification process.'
  },
  product_identification: {
    question:
      'Are parts identified from receipt through storage, processing, packaging, and shipment?',
    describePrompt: 'Describe how parts are labeled across the production flow.'
  },
  lot_integrity_traceability: {
    question:
      'Do you maintain lot identity and traceability throughout production?',
    describePrompt: 'Describe your lot traceability process.'
  },
  process_validation: {
    question:
      'Do you validate special processes (those not verified by downstream inspection) before production use?',
    describePrompt: 'Describe how special processes are validated.'
  },
  process_capability_spc: {
    question:
      'Do you use process operation studies and SPC to monitor process parameters?',
    describePrompt: 'Describe when and how these tools are used.'
  },
  continual_improvement: {
    question:
      'Do you have a system for collecting and acting on employee improvement suggestions?',
    describePrompt: 'Describe how suggestions are submitted and resolved.'
  },
  ppm_otd_metrics: {
    question: 'Are customer PPM and OTD metrics shared with your employees?',
    describePrompt:
      'How are these metrics communicated, and what are the current results vs. goal?'
  },
  customer_audits: {
    question:
      'Has your QMS been audited by a customer or third party (e.g. ISO 9001) in the past 90 days?',
    describePrompt: 'Describe the audit and its results.'
  },
  customer_scorecards: {
    question:
      'Do you review customer scorecards and take corrective action on the findings?',
    describePrompt: 'Describe how scorecards are reviewed and acted on.'
  },
  internal_goals_objectives: {
    question: 'Do you monitor scrap, rework, or internal PPM metrics?',
    describePrompt:
      'Which metrics are tracked, and how are results shared with employees?'
  }
}
